'use memo';

import React, { useContext, useEffect, useState } from 'react';
import { EnvelopeRecipientStatus } from '@urbanx/agx-ui-components';
import {
  AgxButton,
  AgxDivider,
  AgxSpacer,
  AgxTypography,
  useToast,
} from '@urbanx/agx.ui-components2';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import { SentForSigningButtons } from '../../CampaignsList/components/SentForSigningPanel/SentForSigningButtons';
import { Signatory } from '../../CampaignsList/components/SentForSigningPanel/Signatory';
import placeHolderImage from 'assets/images/placeholder-property.png';
import { Breakpoints, ScreenSize } from 'utils/screen';
import { Tabs } from 'types/Tabs';
import './AgencyAgreementSigningPanel.scss';
import { useFormLoader } from 'hooks/useFormLoader';
import { AuthContext } from '@urbanx/agx.auth.client';
import { completeCampaignStep } from 'Api/Campaigns/campaignsApi';
import { useAppSelector } from 'hooks/useAppSelector';
import { isEqual } from 'lodash-es';
import { CampaignDetailModel, FormType } from '@urbanx/agx.campaigns.client';
import {
  getCampaignStageLabel,
  getInvalidSubmissionStage,
} from './AgencyAgreementSigningPanel.helpers';
import { addHorizontalDividersBetweenElements } from '../Common/addHorizontalDividersBetweenElements';

interface AgencyAgreementSigningPanelProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  mobile: boolean;
}

const AgencyAgreementSigningPanel: React.FC<
  AgencyAgreementSigningPanelProps
> = ({ campaign, currentTab }) => {
  const {
    agencyAgreementEnvelope: envelopeSubmission,
    propertyImageUrls: { medium: imageUrl = null } = {},
    address,
    campaignId,
    completionState,
    stage,
  } = campaign;
  const {
    envelopeSubmissionId: envelopeId,
    type,
    recipients: signatories,
  } = envelopeSubmission ?? {};

  const declinedStatus = signatories?.find(
    recipient => recipient.status === EnvelopeRecipientStatus.Declined
  );

  const isMobile = ScreenSize() === Breakpoints.Mobile;
  const isDesktop = ScreenSize() === Breakpoints.Desktop;

  const availableForms = useAppSelector(state => state.forms.availableForms);
  const { loadFormWithConfig } = useFormLoader();
  const { getAuthToken } = useContext(AuthContext);
  const { pushToast } = useToast();

  const [disableInputs, setDisableInputs] = useState(() =>
    getInvalidSubmissionStage(stage)
  );

  useEffect(() => {
    setDisableInputs(getInvalidSubmissionStage(stage));
  }, [stage]);

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  const pushSubmissionErrorToast = () => {
    pushToast({
      type: 'error',
      message: 'Failed to start Agency Agreement submission',
    });
  };

  const beginAgencyAgreementSubmission = async () => {
    const inProgressForm =
      campaign.inProgressForms != null ? campaign.inProgressForms[0] : null;

    if (!campaignId || !inProgressForm?.formId) return;

    const agencyAgreementSubmissionForm = availableForms.find(
      form =>
        form.type === FormType.ListingSubmission &&
        isEqual(form.state, campaign.state)
    );

    if (!agencyAgreementSubmissionForm) {
      pushSubmissionErrorToast();
      return;
    }

    try {
      const authToken = await getAuthToken();

      if (!authToken) {
        setDisableInputs(false);
        return;
      }

      const newForm = await completeCampaignStep(
        authToken,
        campaignId,
        false,
        inProgressForm?.formId
      );

      if (!newForm) {
        pushSubmissionErrorToast();
        setDisableInputs(false);
        return;
      }

      await loadFormWithConfig(newForm);
    } catch (err) {
      pushSubmissionErrorToast();
    }

    setDisableInputs(false);
  };

  const renderSignatories = () => {
    const signatoryComponents = signatories?.map((signatory, i) => {
      if (signatory && envelopeId) {
        return (
          <Signatory
            key={`signatory-${i}`}
            signatory={signatory}
            envelopeSubmissionType={type}
            envelopeId={envelopeId}
            declinedStatus={!!declinedStatus}
          />
        );
      }

      return <></>;
    });

    if (!signatoryComponents) return <></>;

    return [
      <AgxDivider key="initialDivider" variant="horizontal" />,
      ...addHorizontalDividersBetweenElements(signatoryComponents),
    ];
  };

  const StartSubmissionButton = () => {
    return (
      <AgxButton
        id="submit-agreement"
        variant="primary"
        size={'large'}
        stretch
        text="Submit Agreement"
        onClick={beginAgencyAgreementSubmission}
        disabled={
          disableInputs ||
          !!completionState?.completionDate ||
          currentTab === Tabs.Archive
        }
      />
    );
  };

  return (
    <>
      <AgxTypography
        id="aa-signing-panel-address"
        type="heading"
        size="2x-large"
        style={{ textAlign: 'center' }}
      >
        {addressLineOne},
        <br />
        {addressLineTwo}
      </AgxTypography>
      <AgxSpacer
        direction="horizontal"
        contentAlignment="center"
        contentJustification="center"
        className="listing-imageCard"
      >
        <div
          style={{
            backgroundImage: `url("${imageUrl || placeHolderImage}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            aspectRatio: '2/1',
          }}
          className={`imageCard-image ${!!completionState?.completionType ? 'archived' : ''}`}
        >
          <div className="image-tag">
            <AgxTypography
              id="aa-signing-panel-stage"
              type="label"
              size="medium"
              color="var(--brand-text-button-color)"
            >
              {getCampaignStageLabel(campaign.stage)}
            </AgxTypography>
          </div>
        </div>
      </AgxSpacer>
      <AgxSpacer direction="horizontal" gap="sm" contentAlignment="center">
        {envelopeId && (
          <SentForSigningButtons
            currentTab={currentTab}
            campaign={campaign}
            envelopeId={envelopeId}
            isMobile={isMobile}
            isDesktop={isDesktop}
          />
        )}
        <StartSubmissionButton />
      </AgxSpacer>
      <AgxSpacer
        direction="vertical"
        fill
        contentAlignment="center"
        className="signatoriesContainer"
      >
        {renderSignatories()}
      </AgxSpacer>
    </>
  );
};

export default AgencyAgreementSigningPanel;
